package com.evoapps.evoshift.kiosk

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import org.w3c.dom.HTMLElement
import kotlin.js.Promise

/**
 * The browser's own BarcodeDetector (SH-111): Chrome and Edge on Android, ChromeOS and macOS.
 * Where it is missing (Safari, Firefox) the entry is hidden; no JS decoder is bundled. The video
 * plays in an overlay above the app's canvas and stops as soon as a code is read or cancelled.
 */
actual fun platformQrScanner(): QrScanner = WebQrScanner

private object WebQrScanner : QrScanner {
    override val isAvailable: Boolean
        get() = runCatching { hasBarcodeDetector() }.getOrDefault(false)

    override suspend fun scan(): QrScan {
        if (!isAvailable) return QrScan.Unavailable
        val formats = runCatching { supportedFormats().await() }.getOrNull()
        if (formats == null || "qr_code" !in formats) return QrScan.Unavailable
        val stream: dynamic = try {
            userMedia().await()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            val name = e.asDynamic().name as? String
            return if (name == "NotAllowedError" || name == "SecurityError") QrScan.PermissionDenied else QrScan.Unavailable
        }
        val overlay = Overlay(stream)
        try {
            val detector: dynamic = newDetector()
            while (!overlay.cancelled) {
                val codes: Array<dynamic> = runCatching { detector.detect(overlay.video).unsafeCast<Promise<Array<dynamic>>>().await() }
                    .getOrDefault(emptyArray())
                val text = codes.firstNotNullOfOrNull { it.rawValue as? String }
                if (!text.isNullOrEmpty()) return QrScan.Code(text)
                delay(200)
            }
            return QrScan.Cancelled
        } finally {
            overlay.close()
        }
    }
}

private fun hasBarcodeDetector(): Boolean =
    js("typeof window !== 'undefined' && ('BarcodeDetector' in window) && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)") as Boolean

private fun supportedFormats(): Promise<Array<String>> = js("window.BarcodeDetector.getSupportedFormats()").unsafeCast<Promise<Array<String>>>()

private fun newDetector(): dynamic = js("new window.BarcodeDetector({ formats: ['qr_code'] })")

private fun userMedia(): Promise<dynamic> =
    js("navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false })").unsafeCast<Promise<dynamic>>()

/** A full-window black layer with the camera's video and a close button. */
private class Overlay(private val stream: dynamic) {
    var cancelled = false
        private set
    private val root = document.createElement("div") as HTMLElement
    val video = document.createElement("video") as HTMLElement

    init {
        root.setAttribute(
            "style",
            "position:fixed;inset:0;z-index:2147483000;background:#000;display:flex;align-items:center;justify-content:center"
        )
        video.setAttribute("style", "width:100%;height:100%;object-fit:cover")
        video.setAttribute("playsinline", "")
        video.setAttribute("muted", "")
        video.asDynamic().muted = true
        video.asDynamic().srcObject = stream
        val close = document.createElement("button") as HTMLElement
        close.textContent = "✕"
        close.setAttribute("aria-label", "Close")
        close.setAttribute(
            "style",
            "position:absolute;top:16px;right:16px;width:56px;height:56px;border-radius:28px;border:none;" +
                "background:rgba(255,255,255,0.85);font-size:24px;cursor:pointer"
        )
        close.addEventListener("click", { cancelled = true })
        root.appendChild(video)
        root.appendChild(close)
        document.body?.appendChild(root)
        runCatching { video.asDynamic().play() }
    }

    fun close() {
        cancelled = true
        runCatching {
            val tracks: Array<dynamic> = stream.getTracks().unsafeCast<Array<dynamic>>()
            tracks.forEach { it.stop() }
        }
        root.parentNode?.removeChild(root)
        window.focus()
    }
}
